/* UI chrome copy. Catalog content is localized in the data itself (see
   LocalizedText in lib/types) — this file is only the strings the app owns. */

export default {
  brand: 'HexClean',
  tagline: 'Home services, booked in seconds',

  /* The browser tab, the share card, and what a screen reader announces after
     a route change — all three read from `document.title`. Left untranslated,
     an Arabic session narrates every page in English. Keyed by route so the
     tab says which screen you are on, which is the whole point of a title on a
     site people keep open in a background tab. */
  title: {
    suffix: 'HexClean',
    description:
      'Book laundry, home cleaning and car wash at one honest price. Pick a time that suits you, follow it to your door, and pay however you like.',
    landing: 'HexClean — Home services, booked in seconds',
    app: 'Home',
    book: 'Book a service',
    explore: 'Our services',
    join: 'Work with HexClean',
    search: 'Search',
    provider: 'Where to go',
    basket: 'Basket',
    checkout: 'Checkout',
    order: 'Track your order',
    orders: 'Your orders',
    account: 'Account',
    addresses: 'Addresses',
    auth: 'Sign in',
    authAction: 'Your account',
    notFound: 'Page not found',
  },

  nav: {
    home: 'Home',
    services: 'Services',
    orders: 'Orders',
    account: 'Account',
    search: 'Search',
    basket: 'Basket',
  },

  common: {
    back: 'Back',
    close: 'Close',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    saving: 'Saving…',
    done: 'Done',
    edit: 'Edit',
    delete: 'Delete',
    retry: 'Try again',
    seeAll: 'See all',
    apply: 'Apply',
    clear: 'Clear',
    from: 'From',
    free: 'Free',
    optional: 'optional',
    required: 'Required',
    loading: 'Loading',
    offline: 'You are offline',
    offlineBody: 'We will reconnect as soon as you are back.',
    errorTitle: 'Something went wrong',
    errorBody: 'That did not go through. It is usually worth one more try.',
    /* A 401 is not a failure, it is a question — and answering it with
       `errorBody` above told people to retry a request that will never
       succeed however many times they press it (Live-Test P1-2). Addresses,
       orders and saved lists all render this when signed out. */
    signInTitle: 'Sign in to see this',
    signInBody: 'This page is yours. Sign in and it will be here.',
    notFound: 'Not found',
    /* Shown when a route's code could not be downloaded — a dropped
       connection, or a tab left open across a deploy. Deliberately about the
       download rather than about "an error", because reloading genuinely does
       fix it and telling the user so is more use than an apology. */
    crashTitle: 'This screen did not load',
    crashBody: 'Its files could not be downloaded. Reloading almost always fixes it.',
    crashReload: 'Reload',
    updating: 'Getting the latest version…',
    minutes: 'min',
  },

  lp: {
    openApp: 'Open app',
    eyebrow: 'Real professionals, properly checked',
    title: 'Someone reliable, at your door, when you said.',
    sub: 'Laundry, cleaning and car wash, at one honest price. Pick a time that suits you, watch it happen, and pay however you like.',
    ctaGo: 'Start booking',
    ctaBrowse: 'See our services',
    /* Counted from the live catalogue, not typed in. The old trio claimed
       "22+ vetted providers" and "9 service categories" against a catalogue
       that published three — a number nobody re-checks once it ships. */
    statServices: 'Services',
    statRating: 'Average rating',
    statSlots: 'Booking windows daily',

    servicesTitle: 'Things you no longer have to arrange yourself',
    servicesSub: 'Every category runs on the same booking, tracking and payment flow.',

    howTitle: 'How it works',
    howSub: 'Three decisions, then it is somebody else’s problem.',
    step_where: 'Tell us where',
    step_where_body:
      'District, street, building. The details a driver actually reads, so nobody has to phone you from the wrong gate.',
    step_when: 'Pick your slot',
    step_when_body:
      'Real two-hour windows with live availability. If a slot is gone it says so before you choose it, not after.',
    step_done: 'Track it through',
    step_done_body:
      'Every stage timestamped, from accepted to back at your door. Cancel free while the work has not started.',

    trustTitle: 'Why people stay',
    trustSub: 'The parts that decide whether you book a second time.',
    trust_vetted: 'Providers we checked',
    trust_vetted_body:
      'Licensed, insured and reviewed by customers who actually paid. Ratings come from completed orders only.',
    trust_guarantee: 'Something went wrong?',
    trust_guarantee_body:
      'Re-clean, re-collect or refund. You talk to us, not to a provider you have never met.',
    trust_pricing: 'The price you were shown',
    trust_pricing_body:
      'Itemised before you confirm. No arrival surcharge, no rounding up, no line you did not agree to.',
    trust_rebook: 'One tap to repeat',
    trust_rebook_body:
      'Your last order, your saved address and your usual slot are one tap from the home screen.',

    reviewsTitle: 'What people said afterwards',
    reviewsSub: 'From completed orders only. One review per provider, not eight for the same one.',

    finalTitle: 'Get the week off your list.',
    finalSub: 'No account needed to look around.',

    footBlurb: 'Home services across Saudi Arabia, booked in seconds and tracked to the door.',
    footContact: 'Contact',
  },

  region: {
    title: 'Country & language',
    country: 'Country',
    language: 'Language',
    serving: 'Serving',
    /* Stated rather than apologised for. "Only" reads as a limitation; naming
       the market and what is next reads as a company that knows its plan. */
    singleMarket: 'HexClean operates in Saudi Arabia today. More markets are on the way.',
    comingSoon: 'Coming soon',
    change: 'Change country or language',
    /* Read out while the curtain is down. The language name beside it carries
       its own `lang`, so assistive tech pronounces "العربية" in an Arabic
       voice rather than spelling it — this line is the sentence around it. */
    switching: 'Changing language',
  },

  home: {
    greeting: 'What needs doing?',
    deliverTo: 'Service in',
    changeArea: 'Change',
    categories: 'Services',
    reorder: 'Book again',
    reorderSub: 'Reorder in one tap',
    promos: 'Offers',
    promosSub: 'Applied automatically at checkout',
    installTitle: 'Add HexClean to your home screen',
    installBody: 'Faster to open, works offline, no app store.',
    install: 'Add',
    dismiss: 'Not now',
  },

  /* Kept under `explore` because the route is still /explore. The screen is a
     service list now, so the sort, filter and rating keys that ranked shops
     went with the shops. */
  explore: {
    title: 'Services',
    emptyTitle: 'Nothing available here yet',
    emptyBody: 'We are not serving this area yet. Try another district.',
  },

  search: {
    placeholder: 'Search services or items',
    recent: 'Recent',
    popular: 'Popular searches',
    emptyTitle: 'No matches',
    emptyBody: 'Check the spelling, or browse the services instead.',
    hintTitle: 'Search HexClean',
    hintBody: 'Try a service, or something specific like “abaya”.',
    /* The suggestion chips. Translated rather than passed through as English
       keywords: they are the seeds of a query, and a chip that types Latin
       text into an Arabic search field returns nothing and teaches the user
       that search is broken. */
    suggestions: ['Laundry', 'Deep clean', 'Car wash', 'Abaya', 'AC service', 'Water'],
  },

  provider: {
    about: 'About',
    reviews: 'Reviews',
    reviewsCount: '{{count}} reviews',
    menu: 'Services',
    minOrder: 'Min. {{amount}}',
    openUntil: 'Open until {{time}}',
    closed: 'Closed now',
    areas: 'Serving',
    addToBasket: 'Add',
    popular: 'Popular',
    options: 'Options',
    notes: 'Notes for the provider',
    notesPlaceholder: 'Anything they should know?',
    added: 'Added to basket',
    switchProviderTitle: 'Start a new basket?',
    switchProviderBody: 'Your basket has something else in it. Ordering from {{next}} will clear it.',
    switchProviderConfirm: 'Start new basket',
    directions: 'Directions',
    perPiece: 'per piece',
    perKg: 'per kg',
    perHour: 'per hour',
    perVisit: 'per visit',
    perSqm: 'per m²',
    perItem: 'each',
  },

  book: {
    venueTitle: 'Where should this happen?',
    venueWeCome: 'Come to me',
    venueWeComeSub: 'At your parking space, water and power included',
    venueYouCome: 'I will drive in',
    venueYouComeSub: 'At a car wash near you, usually quicker',
    sitesTitle: 'Car washes you can drive to',
    siteTitle: 'Your car wash',
    changeSite: 'Change',
    siteNear: 'Nearest',
    noSites: 'No sites open near you yet',
    noSitesBody: 'Switch to “Come to me” and someone will wash it where it is parked.',
    switchTitle: 'Start a new basket?',
    switchBody: 'Your basket has a different service in it. Adding this will clear it.',
    switchSiteTitle: 'Change car wash?',
    switchSiteBody: 'Prices are set by each wash, so your basket will be cleared.',
  },

  basket: {
    title: 'Basket',
    empty: 'Your basket is empty',
    emptyBody: 'Pick a service and the items you add will show up here.',
    /* A line whose item has left the price list. Two keys chosen in code
       rather than i18next's plural suffixes: nothing else in this bundle uses
       them, they are not configured, and Arabic wants six forms where `_other`
       offers one — so "1 items" here, or worse there, is exactly the detail
       that makes somebody trust the rest of the sentence less. */
    withdrawnOne: 'One item is no longer offered, so we took it out of your basket.',
    withdrawnMany: 'Some items are no longer offered, so we took them out of your basket.',
    browse: 'Browse services',
    subtotal: 'Subtotal',
    delivery: 'Delivery',
    surcharge: 'Evening slot',
    discount: 'Discount',
    /* Inclusive, so it is stated rather than added — see OrderTrack. */
    vatIncluded: 'Includes VAT of {{amount}}',
    wallet: 'Wallet credit',
    refund: 'Refunded credit',
    total: 'Total',
    checkout: 'Checkout',
    belowMin: 'Add {{amount}} more to reach the minimum',
    itemsOne: '{{count}} item',
    itemsOther: '{{count}} items',
    removed: 'Removed',
    undo: 'Undo',
    clear: 'Clear basket',
    clearConfirm: 'Remove everything from your basket?',
    viewBasket: 'View basket',
  },

  checkout: {
    title: 'Checkout',
    who: 'Who is doing it',
    address: 'Address',
    addressAdd: 'Add an address',
    addressChange: 'Change',
    schedule: 'Schedule',
    pickup: 'Pickup',
    delivery: 'Delivery',
    visit: 'Visit',
    noSlots: 'No slots left today',
    slotTaken: 'That slot has just gone',
    payment: 'Payment',
    payCard: 'Card',
    payMada: 'mada',
    payCash: 'Cash on delivery',
    /* Stated rather than offered — placement takes cash only. Says what the
       customer has to do, because 'cash on delivery' alone does not tell
       somebody they need notes on them when the partner arrives. */
    payCashNote: 'Pay the partner directly when the work is done.',
    payCashOnSite: 'Cash at the counter',
    payWallet: 'Wallet',
    payApple: 'Apple Pay',
    useWallet: 'Use wallet credit ({{amount}})',
    /* Shown only when the ceiling actually bound — when a code and a
       balance between them asked for more than we may give away on this
       order. Names the number so it reads as a rule rather than a fault,
       and says which of the two won, because 'some of your credit was not
       used' is the sentence that generates the support ticket. */
    creditCapped:
      'Promo codes and wallet credit share a {{amount}} limit on this order, so part of your balance was not used.',
    creditCappedPromo:
      'Your code used the full {{amount}} of credit allowed on this order, so your wallet balance was not applied.',
    promo: 'Promo code',
    promoPlaceholder: 'Enter a code',
    promoApplied: '{{code}} applied',
    promoRemove: 'Remove',
    placeOrder: 'Place order · {{amount}}',
    placing: 'Placing your order…',
    /* Deliberately does not say what it was before. The customer is being
       asked to accept the number in front of them, and quoting the old one
       invites an argument about a price nobody can still honour. */
    priceMoved: 'The price changed. Check the total and place again.',
    slotGone: 'That time has just been taken. Please choose another.',
    summary: 'Order summary',
    termsNote: 'By placing this order you accept the service terms.',
  },

  order: {
    trackTitle: 'Order {{ref}}',

    /* Rung labels. The general wording lives at the top; a track overrides only
       the rungs it genuinely says differently. See lib/track.ts — the timeline
       asks for the override first and falls back to the general one, so this
       file carries the differences rather than four near-identical copies. */
    step: {
      placed: 'Request placed',
      confirmed: 'Confirmed',
      'on-the-way': 'On the way',
      arrived: 'At your door',
      'picked-up': 'Picked up',
      'in-progress': 'In progress',
      'out-for-delivery': 'Out for delivery',
      delivered: 'Completed',
      /* Terminal, and phrased as the apology it is. "Unfulfilled" is what the
         status is called; it is not what you say to somebody who waited. */
      unfulfilled: 'Nobody was available',

      site: {
        confirmed: 'Booked',
        arrived: 'Checked in',
      },
      collection: {
        /* Two journeys, so each is labelled for its direction — "on the way"
           twice would read as the timeline having stalled and repeated. */
        'on-the-way': 'On the way to collect',
        'picked-up': 'Collected',
        'in-progress': 'Being cleaned',
        'out-for-delivery': 'On the way back',
        delivered: 'Delivered',
      },
      dropoff: {
        'out-for-delivery': 'Out for delivery',
        delivered: 'Delivered',
      },
    },

    note: {
      placed: 'We have sent it to the provider.',
      confirmed: 'The provider has accepted your booking.',
      'on-the-way': 'They are on their way to you.',
      arrived: 'They have arrived.',
      'picked-up': 'Your items are with the driver.',
      'in-progress': 'Work is underway.',
      'out-for-delivery': 'On the way back to you.',
      delivered: 'All done. Thank you.',
      cancelled: 'This order was cancelled.',
      unfulfilled:
        'We could not find anyone free for this slot, so nothing was charged. Try another time and we will look again.',

      site: {
        confirmed: 'Your slot is held. Drive over any time inside it.',
        arrived: 'You are checked in.',
        'in-progress': 'Your car is being worked on.',
        delivered: 'All done. Thank you.',
      },
      collection: {
        'in-progress': 'Your items are being cleaned.',
        delivered: 'Returned. Thank you.',
      },
      dropoff: {
        confirmed: 'Your delivery is scheduled.',
        /* Not "on the way back" — nothing of the customer's ever left. */
        'out-for-delivery': 'Your delivery is on the way.',
        delivered: 'Delivered. Thank you.',
      },
    },

    eta: {
      default: 'Arriving {{when}}',
      /* Nothing arrives on a drive-in booking — the customer does. */
      site: 'Your slot is {{when}}',
    },

    cancelled: 'Cancelled',
    unfulfilled: 'Not fulfilled',
    matchingNote: 'Finding you someone nearby. This usually takes a minute or two.',
    confirmedNamed: '{{name}} accepted and will be there in your slot.',
    confirmedNamedSite: '{{name}} is expecting you in your slot.',
    contact: 'Contact provider',
    cancel: 'Cancel order',
    cancelHold: 'Hold to cancel',
    cancelDone: 'Order cancelled',
    cannotCancel: 'This order has started and can no longer be cancelled online.',
    rate: 'How did it go?',
    rateThanks: 'Thanks for rating',
    reorder: 'Book again',
    receipt: 'Receipt',
    historyTitle: 'Your orders',
    historyEmpty: 'No orders yet',
    historyEmptyBody: 'Once you book something it will live here.',
    active: 'Active',
    past: 'Past',
  },

  account: {
    title: 'Account',
    signIn: 'Sign in',
    signInBody: 'Save addresses, track orders and collect points.',
    signOut: 'Sign out',
    profile: 'Profile',
    name: 'Name',
    namePlaceholder: 'Your name',
    phone: 'Mobile',
    email: 'Email',
    addresses: 'Addresses',
    addressesSub: '{{count}} saved',
    walletTitle: 'Wallet',
    walletSub: 'Credit applied automatically',
    points: '{{count}} points',
    pointsSub: 'Earn 1 point per SAR spent',
    referral: 'Invite a friend',
    referralSub: 'They get SAR 20, you get SAR 20',
    referralCopy: 'Copy code',
    referralCopied: 'Code copied',
    country: 'Country',
    language: 'Language',
    theme: 'Appearance',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeAuto: 'Auto',
    support: 'Help & support',
    about: 'About HexClean',
    version: 'Version',
  },

  auth: {
    title: 'Sign in',
    subtitle: 'Your email and password — or use Google if you prefer.',
    phone: 'Mobile number',
    /* Says what the number is *for*, now that nothing is sent to it. A field
       that used to explain itself by the code arriving on it has to say
       something, or it reads as one more thing being collected for no reason. */
    phoneHint: 'How the driver reaches you. Saudi or Pakistani numbers.',
    country: 'Country',
    or: 'or',
    google: 'Continue with Google',
    email: 'Email',
    password: 'Password',
    withPassword: 'Sign in',
    linkTitle: 'One more step',
    linkSubtitle:
      'Orders reach you by number, so we need yours. Signed in as {{email}}.',
    sending: 'Sending…',
    devAccount: 'Mock build — tap to fill {{email}} / {{password}}',

    /* the mailbox step, which is where verification lives now */
    verifyTitle: 'Confirm your email',
    verifySubtitle: 'We sent a link to {{email}}. Open it, then come back and sign in.',
    verifyHint:
      'Anybody can type an address. The link is how we know it is yours — check the spam folder if it has not arrived.',
    verifyResend: 'Send the link again',
    verifyResent: 'Link sent',

    /* registration, and the recovery that has to exist beside it */
    emailPlaceholder: 'you@example.com',
    confirm: 'Confirm password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    checking: 'Checking…',
    creating: 'Creating your account…',
    passwordHint: 'At least {{min}} characters.',
    mismatch: 'Those two do not match.',

    /* What the create-account button says back when it cannot create one.
       Each names the field and what it wants, because "invalid" on a form of
       five fields is a riddle, not an error. */
    needTitle: 'Nearly — these still need you:',
    needEmail: 'Enter your email address',
    needPassword: 'Choose a password of at least {{min}} characters',
    needConfirm: 'Type the same password again to confirm it',
    needPhone: 'Add your mobile number',
    needPhoneShape: 'Check the number — mobiles here are {{digits}} digits and start with {{leading}}',
    needAddress: 'Add your address — search for it, or type it by hand',
    needArea: 'Choose your district',
    strengthNone: 'No password yet',
    strengthWeak: 'Too short',
    strengthFair: 'Fair password',
    strengthStrong: 'Strong password',
    noAccount: 'New to HexClean?',
    haveAccount: 'Already have an account?',
    createAccount: 'Create an account',
    signIn: 'Sign in',
    signInInstead: 'Sign in instead',
    signUpTitle: 'Create your account',
    signUpSubtitle: 'An email, a password, your number and where we are coming to.',
    almostTitle: 'Almost there',
    almostSubtitle:
      'Your account is created. Orders reach you by number, so we need yours too.',
    forgot: 'Forgotten your password?',
    forgotTitle: 'Reset your password',
    forgotSubtitle: 'We will email you a link to set a new one.',
    sendReset: 'Send the link',
    backToSignIn: 'Back to sign in',
    sentTitle: 'Check your inbox',
    /* "If" is doing real work here: an answer that differed between a
       registered address and an unregistered one would be a way to ask which
       of a list of addresses banks with us. */
    sentSubtitle: 'If {{email}} has an account, a link to reset the password is on its way.',
    sentHint: 'It can take a minute. Check the spam folder if it does not arrive.',
    resendReset: 'Send it again',
    verifiedToast: 'Email confirmed',

    finish: 'Finish signing in',
    startOver: 'Start again',
  },

  authAction: {
    checking: 'Checking that link…',
    badLink: 'That link has already been used, or is not valid.',
    deadTitle: 'That link will not work',
    resetTitle: 'Set a new password',
    resetSubtitle: 'For {{email}}.',
    newPassword: 'New password',
    save: 'Save the password',
    saving: 'Saving…',
    resetDone: 'Password changed. Sign in with it.',
    verifiedTitle: 'Email confirmed',
    verifiedBody: 'Thanks — that address is now confirmed on your account.',
    recoveredTitle: 'Address restored',
    recoveredBody: 'Your account is back on its previous email address.',
    backToSignIn: 'Back to sign in',
  },

  address: {
    title: 'Addresses',
    add: 'Add address',
    editTitle: 'Edit address',
    newTitle: 'New address',
    nickname: 'Nickname',
    nicknamePlaceholder: 'Home, Office, Mum’s',
    labelHome: 'Home',
    labelWork: 'Work',
    labelOther: 'Other',
    area: 'District',
    street: 'Street',
    building: 'Building no.',
    additionalNo: 'Additional no.',
    floor: 'Floor',
    apartment: 'Apartment',
    directions: 'Directions',
    directionsPlaceholder: 'Beige villa, black gate, second from the corner',
    setDefault: 'Set as default',
    default: 'Default',
    emptyTitle: 'No addresses yet',
    emptyBody: 'Add one so providers know where to go.',
    deleteConfirm: 'Delete this address?',
    saved: 'Address saved',

    /* the search-backed field on the registration form */
    searchLabel: 'Your address',
    searchPlaceholder: 'Street, district or landmark',
    useLocation: 'Use my current location',
    locating: 'Finding you…',
    locateFailed: 'We could not read your location. Search for the street instead.',
    noPlaces: 'Nothing found — you can type the address by hand instead.',
    enterByHand: 'Enter it by hand',
    searchInstead: 'Search for it instead',
    streetPlaceholder: 'Street, building and anything a driver needs',
    manualHint: 'Write it however you would tell a driver. The district below is what we route on.',
    checkDistrict: 'Check the district is right — we could not work it out from that.',
    areaPlaceholder: 'Choose your district',
    areasLoading: 'Loading districts…',
    /* The dead end this used to be, said out loud. The district list is the
       server's, and without it the form's last field has nothing in it to
       pick — which is a create-account button that cannot work and, before
       this line existed, would not say so. */
    areasFailed: 'We could not load the district list.',
    /* Sign-in worked and the address did not. Says where to finish the job
       rather than apologising, because the account exists either way. */
    saveLater: 'Your account is ready. Add your address from the Addresses screen.',
  },

  join: {
    title: 'Work with HexClean',
    heroTitle: 'Bring your work. We will bring the customers.',
    heroSub:
      'Jobs come to you with the address, the price and the time already agreed. No haggling on the doorstep, no chasing anyone for cash.',
    howItWorks: 'How it works',
    accountSub: 'Apply as a freelancer, an operator or a company',

    /* The handover. Shown for the moment it takes to leave, and as the fallback
       if the navigation is blocked — so it has to read as a sentence somebody
       could act on rather than as a spinner with words. */
    handoffTitle: 'Taking you to the partner app',
    handoffBody:
      'Applications live in the HexClean partner app — it is where you send your documents, and where you will pick up jobs once you are approved.',
    handoffAction: 'Continue',

    stat_work: 'Work you choose',
    stat_work_body: 'Set your districts and your hours. Turn jobs down without penalty.',
    stat_pay: 'Paid weekly',
    stat_pay_body: 'Every Sunday, to your bank account, itemised per job. No cash handling.',
    stat_kit: 'We handle the rest',
    stat_kit_body: 'Bookings, payments, customer support and the refund when it goes wrong.',

    kind_freelancer: 'Freelance professional',
    kind_freelancer_body:
      'You work on your own and want the bookings to come to you instead of being chased through WhatsApp.',
    kind_freelancer_eg: 'Cleaners · handymen · stylists · groomers',

    kind_mobile: 'Mobile operator',
    kind_mobile_body:
      'You travel to the customer with your own equipment and want to be findable by the people parked around you.',
    kind_mobile_eg: 'Home car washers · mobile grooming vans · water delivery',

    kind_company: 'Registered business',
    kind_company_body:
      'You have a CR, a team and often premises. You want the quiet hours filled without discounting to do it.',
    kind_company_eg: 'Car washes · laundries · AC and pest companies · salons',

    step_apply: 'Apply',
    step_apply_body: 'Two minutes. Name, number, what you do and where. No account needed.',
    step_check: 'We check',
    step_check_body:
      'Identity and, for a business, the CR. Two working days, and we call you either way.',
    step_onboard: 'Get set up',
    step_onboard_body:
      'A short walkthrough of the partner app, pricing and what our guarantee commits you to.',
    step_earn: 'Start taking jobs',
    step_earn_body: 'Your first booking usually lands the same week you go live.',

    legalNote:
      'Applying does not create a contract. Nothing is charged to you at any stage, and we never ask a partner for a joining fee.',

    form_freelancer_lede: 'Tell us what you do and where. We will call you within two working days.',
    form_mobile_lede:
      'Tell us what you bring with you. The kit you own decides which jobs we can send.',
    form_company_lede:
      'Tell us about the business. The CR number is the one thing we cannot approve you without.',

    fullName: 'Full name',
    fullNamePlaceholder: 'As it appears on your ID',
    phone: 'Mobile number',
    phoneHint:
      'We call this number. Make sure it takes calls, not just WhatsApp. Saudi or Pakistani.',
    email: 'Email',
    /* Not a second contact detail. It is the credential — this is what a
       partner signs in with once an operator approves them, and it is the
       address their account is matched against. */
    emailHint: 'This becomes your sign-in. Use one you can open.',

    businessTitle: 'The business',
    businessName: 'Trading name',
    crNumber: 'Commercial Registration',
    crHint: '10 digits, as issued by the Ministry of Commerce.',
    siteAddress: 'Site customers can drive to',
    siteAddressPlaceholder: 'Street and district',
    siteAddressHint: 'Only if you want walk-in bookings as well as call-outs.',

    aboutTitle: 'About your work',
    years: 'Years doing this',
    hasEquipment: 'Do you bring your own equipment?',
    hasEquipmentHint: 'Pressure washer, vacuum, water tank — whatever your trade needs.',
    hasTransport: 'Do you have your own transport?',
    hasTransportHint: 'A car or van you can reach a customer in.',

    servicesTitle: 'What can you take on?',
    servicesHint: 'Pick everything you do properly. You can change this later.',
    areasTitle: 'Where will you work?',
    /* Was "leave all of them unpicked if you will go wherever we send you",
       which invited an applicant into a `400`: `PartnerApplication.Validate` in
       Go refuses an empty `areas` outright. Copy that contradicts the server is
       worse than no copy — it tells somebody the thing that will fail is fine. */
    areasHint: 'Pick everywhere you can work. At least one.',

    note: 'Anything else',
    notePlaceholder: 'Certificates, team size, the hours that suit you',

    yes: 'Yes',
    no: 'No',
    consent: 'By applying you agree we may contact you and verify what you have told us.',
    submit: 'Send application',
    sending: 'Sending…',

    doneTitle: 'Application received',
    doneBody:
      'A person reads every one of these. We will call you within two working days, whether the answer is yes or no.',
    doneRef: 'Your reference',
    doneNext: 'Keep this number to hand if you call us before we call you.',
    another: 'Apply as something else',
  },

  pwa: {
    updateTitle: 'A new version is ready',
    updateBody: 'Reload to get the latest.',
    updateAction: 'Reload',
    offlineReady: 'Ready to work offline',
    /* The dialog, which is a different case from the bar above. The bar says
       "there is a newer worker waiting"; these say "the build you are looking
       at is not the one on the server", which is the one people were fixing by
       clearing their browser cache. */
    staleTitle: 'A new version is available',
    staleBody: 'This tab is still running an older version of the app. Reload to get the latest — your basket is kept.',
    staleAction: 'OK',
  },

  a11y: {
    increase: 'Increase quantity',
    decrease: 'Decrease quantity',
    ratingOf: 'Rated {{rating}} out of 5',
    rate: 'Choose a rating',
    starsOf: '{{count}} stars',
    closeSheet: 'Close',
    dragHandle: 'Drag to resize, or press Escape to close',
    primaryNav: 'Main',
    notifications: 'Notifications',
    dismiss: 'Dismiss',
  },
} as const
